import { ArtifactItemState } from '../../types/artifactItems';
import { apiClient } from '../apiClient';


class StateService {
  static async getStatesByArtifact(artifactId: string): Promise<ArtifactItemState[]> {
    try {
      // Only log in development mode and sample to reduce verbosity
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log('🔄 [StateService] Fetching states for artifact (sampled):', artifactId);
      }
      const url = `/artifacts/${artifactId}/states`;
      
      const response = await apiClient.get(url);
      
      // Handle different response formats
      let states: ArtifactItemState[] = [];
      
      if (Array.isArray(response.data)) {
        states = response.data;
      } else if (response.data && Array.isArray(response.data.states)) {
        states = response.data.states;
      } else if (response.data && Array.isArray(response.data.items)) {
        states = response.data.items;
      } else {
        if (process.env.NODE_ENV === 'development') {
          console.warn('⚠️ [StateService] Unexpected response format:', response.data);
        }
      }
      
      // Only log in development mode and sample
      if (process.env.NODE_ENV === 'development' && Math.random() < 0.1) {
        console.log(`✅ [StateService] Returning ${states.length} states for artifact ${artifactId}`);
      }
      if (states.length === 0 && process.env.NODE_ENV === 'development') {
        console.warn('⚠️ [StateService] No states found for artifact:', artifactId);
      }
      
      return states;
    } catch (error: any) {
      console.error('❌ [StateService] Failed to fetch states:', error);
      console.error('❌ [StateService] Error details:', {
        message: error?.message,
        response: error?.response?.data,
        status: error?.response?.status,
        url: error?.config?.url,
      });
      throw error; // Re-throw so the caller can handle it
    }
  }

  static async createState(
    artifactId: string,
    state: Partial<ArtifactItemState>
  ): Promise<ArtifactItemState> {
    try {
      // Validate required fields
      if (!state.name || !state.name.trim()) {
        throw new Error('State name is required');
      }
      
      // Clean up the state data - remove fields that backend doesn't accept
      // The backend removes: artifact_ref, demand_type, demand_flow_type, state_metadata, state_rules, state_parameters, validation_rules
      const cleanedState: Record<string, any> = {
        name: state.name.trim(),
        artifact_id: artifactId, // REQUIRED: artifact_id must be included in request body
      };
      
      // Add optional fields only if they are defined
      if (state.description !== undefined) {
        cleanedState.description = state.description || null;
      }
      if (state.state_type !== undefined) {
        cleanedState.state_type = state.state_type;
      }
      if (state.is_required !== undefined) {
        cleanedState.is_required = state.is_required;
      }
      if (state.is_initial !== undefined) {
        cleanedState.is_initial = state.is_initial;
      }
      if (state.is_final !== undefined) {
        cleanedState.is_final = state.is_final;
      }
      if (state.is_improvement !== undefined) {
        cleanedState.is_improvement = state.is_improvement;
      }
      if (state.is_resource_active !== undefined) {
        cleanedState.is_resource_active = state.is_resource_active;
      }
      // Add prime_state_ref if provided (for linking to prime states)
      if ((state as any).prime_state_ref !== undefined) {
        cleanedState.prime_state_ref = (state as any).prime_state_ref;
      }
      
      console.log('🔄 [StateService] Creating state for artifact:', artifactId);
      console.log('🔄 [StateService] State data:', cleanedState);
      
      const response = await apiClient.post(`/artifacts/${artifactId}/states`, cleanedState);
      
      // Handle backend serialization issue where UUIDs might be returned as UUID objects instead of strings
      // Convert any UUID objects to strings in the response
      const responseData = response.data;
      if (responseData) {
        if (responseData.id && typeof responseData.id !== 'string') {
          responseData.id = String(responseData.id);
        }
        if (responseData.artifact_id && typeof responseData.artifact_id !== 'string') {
          responseData.artifact_id = String(responseData.artifact_id);
        }
      }
      
      console.log('✅ [StateService] State created successfully:', responseData);
      return responseData;
    } catch (error: any) {
      console.error('❌ [StateService] Failed to create state:', error);
      console.error('❌ [StateService] Error response:', error.response?.data);
      console.error('❌ [StateService] Error status:', error.response?.status);
      
      // Extract validation errors from response
      // FastAPI returns validation errors in error.response.data.detail as an array
      const errorDetail = error.response?.data?.detail;
      
      // Handle 400 errors that might be due to backend serialization issues
      // Sometimes the backend creates the resource but fails to serialize the response
      if (error.response?.status === 400) {
        const errorMessage = typeof errorDetail === 'string' ? errorDetail : error.message || 'Bad request';
        
        // Check if this is a UUID serialization error (backend bug)
        // The backend returns UUID objects but Pydantic expects strings
        if (errorMessage.includes('string_type') && errorMessage.includes('UUID')) {
          console.warn('[StateService] Backend UUID serialization issue detected. State may have been created despite the error.');
          console.warn('[StateService] This is a backend bug - UUID fields should be converted to strings in the response model.');
          throw new Error('Backend serialization error: The state may have been created successfully, but the backend failed to serialize the response. Please refresh the page to see if the state was created.');
        }
        
        throw new Error(errorMessage);
      }
      
      if (error.response?.status === 422 && Array.isArray(errorDetail)) {
        // Format Pydantic validation errors
        const errorMessages = errorDetail.map((err: any) => {
          if (typeof err === 'string') {
            return err;
          }
          // Pydantic validation error format: { loc: ['field_name'], msg: 'error message', type: 'error_type' }
          const field = Array.isArray(err.loc) ? err.loc.join('.') : 'unknown';
          const message = err.msg || err.message || 'validation error';
          return `${field}: ${message}`;
        });
        const errorMessage = `Validation errors: ${errorMessages.join(', ')}`;
        throw new Error(errorMessage);
      }
      
      // Handle other error formats
      const errorMessage = typeof errorDetail === 'string' 
        ? errorDetail 
        : error.response?.data?.message || error.message || 'Failed to create state';
      
      throw new Error(errorMessage);
    }
  }

  static async updateState(
    artifactId: string,
    stateId: string,
    state: Partial<ArtifactItemState>
  ): Promise<ArtifactItemState> {
    const response = await apiClient.put(`/artifacts/${artifactId}/states/${stateId}`, state);
    return response.data;
  }

  static async deleteState(stateId: string, artifactId: string): Promise<void> {
    await apiClient.delete(`/artifacts/${artifactId}/states/${stateId}`);
  }

  static async createStatesFromRecommendations(
    artifactId: string,
    states: { artifact_ref: string; prime_state_ref: string }[]
  ): Promise<ArtifactItemState[]> {
    const response = await apiClient.post(`/artifacts/${artifactId}/states/from-prime/bulk`, { states });
    return response.data;
  }
}

export { StateService };
